import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  HelpOutline,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  AccountTree as WorkflowIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  TipsAndUpdates as TipsIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  AddCircle as AddCircleIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";

const EditWorkflowUserGuide = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          maxHeight: "90vh",
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
          color: theme.palette.info.contrastText,
          fontWeight: 700,
          fontSize: "1.5rem",
          py: 3,
          px: 4,
          position: "relative",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: `linear-gradient(45deg, ${theme.palette.info.light}, ${theme.palette.info.main})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: theme.shadows[3],
            }}
          >
            <HelpOutline
              sx={{ color: theme.palette.info.contrastText, fontSize: 24 }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t("editWorkFlow.userGuideTitle")}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {t("editWorkFlow.userGuideSubtitle")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.info.contrastText,
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ maxHeight: "70vh", overflowY: "auto", p: 4 }}>
          {/* Overview Section */}
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.1),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              color="info.main"
              sx={{ fontWeight: 600, mb: 2 }}
            >
              {t("editWorkFlow.guideScope")}
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              {t("editWorkFlow.guideScopeText")}
            </Typography>
          </Box>

          {/* Section 1: Before You Start */}
          <Accordion
            defaultExpanded
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <VisibilityIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("editWorkFlow.guideSection1Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                {t("editWorkFlow.guideSection1Text")}
              </Typography>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("editWorkFlow.guideTwoAreas")}
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <WorkflowIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          <strong>{t("editWorkFlow.guideCanvas")}</strong>{" "}
                          {t("editWorkFlow.guideCanvasText")}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EditIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          <strong>{t("editWorkFlow.guideEditor")}</strong>{" "}
                          {t("editWorkFlow.guideEditorText")}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 2: Open Workflow */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <WorkflowIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("editWorkFlow.guideSection2Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="1" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("editWorkFlow.guideStep2_1")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="2" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("editWorkFlow.guideStep2_2")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="3" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("editWorkFlow.guideStep2_3")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="4" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("editWorkFlow.guideStep2_4")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="5" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("editWorkFlow.guideStep2_5")}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>

              <Box
                sx={{
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  borderRadius: 1,
                  p: 2,
                  mt: 2,
                  display: "flex",
                  gap: 1.5,
                }}
              >
                <WarningIcon color="warning" />
                <Box>
                  <Typography
                    variant="body2"
                    color="warning.main"
                    sx={{ fontWeight: 600 }}
                  >
                    {t("editWorkFlow.guideNote")}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t("editWorkFlow.guideNoteText")}
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 3: Update Step Basics */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <SettingsIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("editWorkFlow.guideSection3Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                {t("editWorkFlow.guideSection3Text")}
              </Typography>

              <Grid container spacing={2}>
                <Grid size={6}>
                  <Box
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      height: "100%",
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <GroupIcon color="primary" fontSize="small" />
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: "primary.main" }}
                      >
                        {t("editWorkFlow.guideAuthorizedGroups")} *
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {t("editWorkFlow.guideAuthorizedGroupsText")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Box
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      height: "100%",
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <EditIcon color="primary" fontSize="small" />
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: "primary.main" }}
                      >
                        {t("editWorkFlow.guideActionName")} *
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {t("editWorkFlow.guideActionNameText")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Box
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      height: "100%",
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <InfoIcon color="primary" fontSize="small" />
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: "primary.main" }}
                      >
                        {t("editWorkFlow.guideStatus")} *
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {t("editWorkFlow.guideStatusText")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Box
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      height: "100%",
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <CheckCircleIcon color="primary" fontSize="small" />
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: "primary.main" }}
                      >
                        {t("editWorkFlow.guideSelfActionable")}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {t("editWorkFlow.guideSelfActionableText")}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  borderRadius: 1,
                  p: 2,
                  mt: 3,
                  display: "flex",
                  gap: 1.5,
                }}
              >
                <TipsIcon color="success" />
                <Box>
                  <Typography
                    variant="body2"
                    color="success.main"
                    sx={{ fontWeight: 600 }}
                  >
                    {t("editWorkFlow.guideTip")}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t("editWorkFlow.guideTipText")}
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 4: Manage Step Inputs */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <AssignmentIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("editWorkFlow.guideSection4Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                {t("editWorkFlow.guideSection4Text")}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <AddCircleIcon color="secondary" />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "secondary.main" }}
                  >
                    {t("editWorkFlow.guideAddField")}
                  </Typography>
                </Box>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Chip label="1" color="secondary" size="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          {t("editWorkFlow.guideAddFieldStep1")}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Chip label="2" color="secondary" size="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          {t("editWorkFlow.guideAddFieldStep2")}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Chip label="3" color="secondary" size="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          {t("editWorkFlow.guideAddFieldStep3")}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Chip label="4" color="secondary" size="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          {t("editWorkFlow.guideAddFieldStep4")}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Chip label="5" color="secondary" size="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          {t("editWorkFlow.guideAddFieldStep5")}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <ArrowUpwardIcon color="secondary" fontSize="small" />
                    <ArrowDownwardIcon color="secondary" fontSize="small" />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "secondary.main" }}
                  >
                    {t("editWorkFlow.guideReorderFields")}
                  </Typography>
                </Box>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <ArrowUpwardIcon color="action" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          {t("editWorkFlow.guideReorderStep1")}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ArrowDownwardIcon color="action" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          {t("editWorkFlow.guideReorderStep2")}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <DeleteIcon color="error" />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "error.main" }}
                  >
                    {t("editWorkFlow.guideRemoveField")}
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {t("editWorkFlow.guideRemoveFieldText")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 5: Save Changes */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <SaveIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("editWorkFlow.guideSection5Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="1" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("editWorkFlow.guideSaveStep1")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="2" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("editWorkFlow.guideSaveStep2")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="3" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("editWorkFlow.guideSaveStep3")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="4" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("editWorkFlow.guideSaveStep4")}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Limitations */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <ErrorIcon color="error" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("editWorkFlow.guideLimitationsTitle")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <ErrorIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("editWorkFlow.guideLimitation1")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ErrorIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("editWorkFlow.guideLimitation2")}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Troubleshooting */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.warning.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <WarningIcon color="warning" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("editWorkFlow.guideTroubleshootingTitle")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Box sx={{ "& > *": { mb: 2 } }}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.error.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    borderRadius: 1,
                    display: "flex",
                    gap: 1.5,
                  }}
                >
                  <ErrorIcon color="error" fontSize="small" />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "error.main", mb: 1 }}
                    >
                      {t("editWorkFlow.guideTroubleshooting1Title")}
                    </Typography>
                    <Typography variant="body2">
                      {t("editWorkFlow.guideTroubleshooting1Text")}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.warning.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    borderRadius: 1,
                    display: "flex",
                    gap: 1.5,
                  }}
                >
                  <WarningIcon color="warning" fontSize="small" />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "warning.main", mb: 1 }}
                    >
                      {t("editWorkFlow.guideTroubleshooting2Title")}
                    </Typography>
                    <Typography variant="body2">
                      {t("editWorkFlow.guideTroubleshooting2Text")}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    borderRadius: 1,
                    display: "flex",
                    gap: 1.5,
                  }}
                >
                  <InfoIcon color="info" fontSize="small" />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "info.main", mb: 1 }}
                    >
                      {t("editWorkFlow.guideTroubleshooting3Title")}
                    </Typography>
                    <Typography variant="body2">
                      {t("editWorkFlow.guideTroubleshooting3Text")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Example */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.success.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.success.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("editWorkFlow.guideExampleTitle")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Box
                sx={{
                  p: 3,
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  borderRadius: 2,
                }}
              >
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Chip label="1" color="success" size="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1">
                          {t("editWorkFlow.guideExampleStep1")}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Chip label="2" color="success" size="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1">
                          {t("editWorkFlow.guideExampleStep2")}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Chip label="3" color="success" size="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1">
                          {t("editWorkFlow.guideExampleStep3")}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Chip label="4" color="success" size="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1">
                          {t("editWorkFlow.guideExampleStep4")}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Completion */}
          <Box
            sx={{
              p: 4,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              borderRadius: 2,
              textAlign: "center",
            }}
          >
            <Typography
              variant="h6"
              color="primary"
              sx={{ fontWeight: 700, mb: 2 }}
            >
              🎉 {t("editWorkFlow.guideCompletionTitle")}
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              {t("editWorkFlow.guideCompletionText")}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.05) }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          color="info"
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("userGuide.closeButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditWorkflowUserGuide.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EditWorkflowUserGuide;
